import * as Sentry from "@sentry/react-native";
import { useQuery } from "@tanstack/react-query";
import { router } from "expo-router";
import { useField, useForm } from "mobx-easy-form";
import { observer } from "mobx-react";
import { FormattedMessage } from "react-intl";
import { Linking, StyleSheet } from "react-native";
import * as yup from "yup";
import { Spacer } from "~/components/Spacer";
import { ButtonV2 } from "~/components/V2/Button";
import { ErrorViewV2 } from "~/components/V2/ErrorViewV2";
import { SpeciesCardSimple } from "~/components/V2/SpeciesCardSimple";
import { SpinnerV2 } from "~/components/V2/SpinnerV2";
import { BodyText } from "~/components/V2/Text/BodyText";
import { BrandText } from "~/components/V2/Text/BrandText";
import { TextInputV2 } from "~/components/V2/TextInputV2";
import { View } from "~/components/V2/View";
import { useErrorAlertV2 } from "~/components/V2/error-alert-v2";
import { useClinicPhoneNumber } from "~/hooks/useClinicPhoneNumber";
import type { SpeciesInstance } from "~/mobx/entities/species/Species";
import { useStore } from "~/mobx/utils/useStore";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { getPhoneNumberWithCountryCode } from "~/utils/getPhoneNumberWithCountryCode";
import { bookingDataLayerPush } from "~/utils/logBookingEvent";
import { useProgressBarStep } from "./useProgressBarStep";
import { useStandaloneBookingFlow } from "./useStandaloneBookingFlow";
import { withBookingData } from "./withBookingData";

const S = StyleSheet.create({
  speciesContainer: { gap: styleConstantsV2.spacing8 },
});

interface AddNewPetScreenWithSpeciesProps {
  species: SpeciesInstance[];
  shouldAnimateProgressBar?: boolean;
}

const AddNewPetScreenWithSpecies = observer(
  function AddNewPetScreenWithSpecies({
    species,
  }: AddNewPetScreenWithSpeciesProps) {
    const { isNewUser, isStandAloneBookingFlow, clinicId } =
      useStandaloneBookingFlow();
    useProgressBarStep(2);

    const { clinicPhoneNumber, clinicPhoneNumberCountryCode } =
      useClinicPhoneNumber();

    const speciesIdList = species.map((specie) => {
      return specie.id;
    }); // e.g ["1053001","1053002","1053003"]

    const store = useStore();
    const errorAlert = useErrorAlertV2();

    const form = useForm({
      onSubmit: async ({ values }) => {
        try {
          const name = values.name;
          const speciesId = values.speciesId;

          // remove this was used to create pet
          //genderId: "0", // default value - we are sending this to not break the API

          // Save patient data to the store
          // we will use this data at the overview screen to create new pet before booking
          store.bookingStore.setNewPet({
            newPetName: name,
            newPetSpeciesId: speciesId,
          });

          store.bookingStore.setPatientId("000000"); // "000000" is the indicator that we are creating a new pet
          store.bookingStore.setPatientSpeciesId(speciesId);

          if (isStandAloneBookingFlow) {
            // Log the event to the booking data layer
            bookingDataLayerPush({
              event: "patient_create_web_app",
            });

            router.navigate({
              pathname: "/(standalone)/booking/appointment-type",
              params: {
                clinicId,
                isNewUser: isNewUser ? "1" : "0",
              },
            });
          } else {
            router.navigate(`/(app)/book-appointment/appointment-type`);
          }
        } catch (error) {
          console.log(error);
          errorAlert(error);
          Sentry.captureException(error);
        }
      },
    });

    const name = useField({
      form,
      id: "name",
      initialValue: "",
      validationSchema: yup.string().required(
        intl.formatMessage({
          id: "I2osjB",
          defaultMessage: "Please provide the name of your animal.",
          description: "Error message when name is not provided",
        }),
      ),
    });

    const speciesId = useField({
      form,
      id: "speciesId",
      initialValue: "",
      validationSchema: yup
        .string()
        .required(
          intl.formatMessage({
            id: "YDTA5Q",
            defaultMessage: "Please select a species for your pet",
            description: "Error message when species is not selected",
          }),
        )
        .oneOf(speciesIdList),
    });

    return (
      <View paddingHorizontal24 flex colorWhite>
        <View paddingVertical40>
          <BrandText sizeH1 weightExtraBold alignCenter colorBlack>
            <FormattedMessage
              defaultMessage="Who is the appointment for?"
              id="qa/1La"
              description="Title asking the user to add a new pet for the appointment."
            />
          </BrandText>
          <Spacer medium />
        </View>
        <BrandText sizeH3 colorBlack weightBold>
          <FormattedMessage
            defaultMessage="What is your pet’s name?"
            id="Tk09uv"
            description="Label prompting the user to enter their pet’s name."
          />
        </BrandText>
        <Spacer small />
        <TextInputV2
          value={name.state.value}
          onChangeText={name.actions.onChange}
          onFocus={name.actions.onFocus}
          onBlur={name.actions.onBlur}
          label={intl.formatMessage({
            id: "tdFh9U",
            defaultMessage: "Pet’s name",
            description: "Label for the pet’s name input field.",
          })}
          isError={!!name.computed.ifWasEverBlurredThenError}
          textBelowComponent={
            name.computed.ifWasEverBlurredThenError && (
              <BodyText sizeSmall colorError>
                {name.computed.error}
              </BodyText>
            )
          }
        />

        <Spacer extraExtraLarge />

        <BrandText sizeH3 colorBlack weightBold>
          <FormattedMessage
            defaultMessage="What type of animal is your pet?"
            id="R7Y2Go"
            description="Text prompting the user to select the type of animal their pet is."
          />
        </BrandText>
        <Spacer medium />
        <View flexDirectionRow style={S.speciesContainer}>
          {species.map((specie) => {
            return (
              <SpeciesCardSimple
                isError={!!speciesId.computed.ifWasEverFocusedThenError}
                key={specie.id}
                specie={specie}
                isSelected={speciesId.state.value === specie.id}
                onPress={(selectedSpecieId) => {
                  speciesId.actions.onFocus();
                  speciesId.actions.onChange(selectedSpecieId);
                }}
              />
            );
          })}
        </View>
        <Spacer extraSmall />
        {speciesId.computed.ifWasEverFocusedThenError && (
          <BodyText sizeSmall colorError>
            {speciesId.computed.error}
          </BodyText>
        )}
        <Spacer extraExtraLarge />
        {!!clinicPhoneNumber && !!clinicPhoneNumberCountryCode && (
          <>
            <Spacer small />
            <View paddingHorizontal24>
              <BodyText alignCenter sizeExtraLarge weightRegular colorBlack>
                <FormattedMessage
                  defaultMessage="If your pet belongs to a different species, please contact us at <clinicPhoneNumberText>{clinicPhoneNumber}</clinicPhoneNumberText> to book an appointment."
                  id="DuyxVu"
                  description="Text informing the user to contact the clinic if their pet belongs to a different species."
                  values={{
                    clinicPhoneNumber: clinicPhoneNumber,
                    clinicPhoneNumberText: (chunks) => {
                      return (
                        <BodyText
                          alignCenter
                          sizeExtraLarge
                          weightRegular
                          colorPrimary
                          underline
                          onPress={() => {
                            // Log the event to the booking data layer
                            bookingDataLayerPush({
                              event: "call_clinic_phone_web_app",
                              ga_action: "call_clinic_phone",
                              ga_label: "AddNewPetScreen",
                            });

                            Linking.openURL(
                              `tel:${getPhoneNumberWithCountryCode(clinicPhoneNumberCountryCode, clinicPhoneNumber)}`,
                            );
                          }}
                        >
                          {chunks}
                        </BodyText>
                      );
                    },
                  }}
                />
              </BodyText>
            </View>
          </>
        )}
        <Spacer extraExtraLarge />
        <View flex flexDirectionColumnReverse paddingVertical32>
          <ButtonV2
            disabled={form.computed.isError && form.state.submitCount > 0}
            title={intl.formatMessage({
              id: "3Wcqsy",
              defaultMessage: "Next",
              description: "Next button",
            })}
            primary
            medium
            onPress={form.actions.submit}
          />
        </View>
      </View>
    );
  },
);

const AddNewPetScreen = observer(function AddNewPetScreen() {
  const store = useStore();

  const query = useQuery({
    queryKey: ["species"],
    queryFn: async () => {
      const species = await store.speciesStore.getSpeciesList();
      return species;
    },
  });

  if (query.isLoading) {
    return (
      <View flex colorWhite>
        <View padding32 centerContent>
          <SpinnerV2 />
        </View>
      </View>
    );
  }

  if (query.isError) {
    return (
      <View flex colorWhite>
        <View centerContent paddingHorizontal24>
          <ErrorViewV2
            error={query.error}
            title={intl.formatMessage({
              id: "+KgYMC",
              defaultMessage: "Something went wrong",
              description: "Error message title when fetching species list",
            })}
          />
          <ButtonV2
            onPress={query.refetch}
            title={intl.formatMessage({
              id: "E71+2H",
              defaultMessage: "Try Again!",
              description: "Button to retry fetching species list",
            })}
          />
        </View>
      </View>
    );
  }

  if (!query.data) {
    throw new Error("No species inside AddNewPetScreen");
  }

  return <AddNewPetScreenWithSpecies species={query.data} />;
});

// If any required data from earlier in the booking process is missing, redirect to the first step
export const AddNewPetScreenWithBookingData = withBookingData(AddNewPetScreen, [
  "clinicId",
]);
